import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Lock, ChevronLeft, Star } from 'lucide-react'
import { chapter1, chapter2, chapter3, chapter4, chapter5, chapter6 } from '@content/algoquest'
import type { Chapter } from '@code-arcade/engine'
import { CutscenePanel } from '@/components/game/CutscenePanel'
import { useAuthStore } from '@/stores/auth.store'
import { useProgressStore } from '@/stores/progress.store'

const CHAPTERS: Chapter[] = [
  chapter1,
  chapter2,
  chapter3,
  chapter4,
  chapter5,
  chapter6,
]

function isChapterLocked(ch: Chapter, progressMap: Record<string, unknown>): boolean {
  if (ch.number === 1) return false
  const prev = CHAPTERS.find((c) => c.number === ch.number - 1)
  if (!prev || prev.challengeIds.length === 0) return true
  return prev.challengeIds.some((id) => !progressMap[id])
}

type ViewState =
  | { kind: 'map' }
  | { kind: 'cutscene'; chapter: Chapter }
  | { kind: 'challenges'; chapter: Chapter }

function StarBadge({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3].map((n) => (
        <Star
          key={n}
          size={12}
          className={n <= count ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700 fill-gray-700'}
        />
      ))}
    </div>
  )
}

export default function Play() {
  const navigate = useNavigate()
  const { questId } = useParams<{ questId: string }>()
  const [view, setView] = useState<ViewState>({ kind: 'map' })

  const { user } = useAuthStore()
  const { progressMap, fetchProgress } = useProgressStore()

  useEffect(() => {
    if (user?.id) fetchProgress(user.id)
  }, [user?.id, fetchProgress])

  const handleChapterClick = (ch: Chapter) => {
    if (isChapterLocked(ch, progressMap)) return
    if (ch.intro.length > 0) {
      setView({ kind: 'cutscene', chapter: ch })
    } else {
      setView({ kind: 'challenges', chapter: ch })
    }
  }

  return (
    <main className="min-h-screen bg-arcade-bg text-white">
      <header className="border-b border-arcade-border px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => {
            if (view.kind !== 'map') {
              setView({ kind: 'map' })
            } else {
              navigate('/games')
            }
          }}
          className="p-2 rounded-lg text-gray-400 hover:text-white transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <div>
          <p className="text-xs text-gray-500 font-mono uppercase tracking-widest">
            {questId ?? 'algoquest'}
          </p>
          <h1 className="text-lg font-bold text-white">
            {view.kind === 'map'
              ? 'World Map'
              : view.kind === 'cutscene'
              ? 'Chapter Intro'
              : `Chapter ${view.chapter.number} — ${view.chapter.title}`}
          </h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* ── World Map ── */}
        {view.kind === 'map' && (
          <>
            <p className="text-sm text-gray-500 mb-8 font-mono">
              Dev Lama's journey — complete each chapter to unlock the next
            </p>
            <div className="flex flex-col gap-4">
              {CHAPTERS.map((ch) => {
                const locked = isChapterLocked(ch, progressMap)
                const completed = ch.challengeIds.filter((cid) => !!progressMap[cid])
                const total = ch.challengeIds.length
                const allDone = total > 0 && completed.length === total

                return (
                  <button
                    key={ch.id}
                    onClick={() => handleChapterClick(ch)}
                    disabled={locked}
                    className={`w-full text-left bg-arcade-surface border rounded-xl p-5 transition-all ${
                      locked
                        ? 'border-arcade-border opacity-40 cursor-not-allowed'
                        : allDone
                        ? 'border-arcade-neon/40 hover:border-arcade-neon cursor-pointer'
                        : 'border-arcade-border hover:border-arcade-accent cursor-pointer'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-gray-500">CH {ch.number}</span>
                          <span className="text-xs font-bold text-gray-300">{ch.company}</span>
                        </div>
                        <h3 className="font-bold text-white text-lg mb-1">{ch.title}</h3>
                        <p className="text-sm text-gray-400">{ch.conflict}</p>
                        <p className="text-xs text-gray-600 mt-1 font-mono">{ch.devTitle}</p>
                        {total > 0 && !locked && (
                          <p className="text-xs text-gray-500 mt-2 font-mono">
                            {completed.length}/{total} challenges complete
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 mt-1">
                        {locked ? (
                          <Lock size={16} className="text-gray-600" />
                        ) : allDone ? (
                          <span className="text-xs px-2 py-1 bg-arcade-neon/10 text-arcade-neon rounded-full font-mono">
                            ✓ DONE
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 bg-arcade-accent/20 text-violet-300 rounded-full font-mono">
                            PLAY
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </>
        )}

        {/* ── Chapter Cutscene ── */}
        {view.kind === 'cutscene' && (
          <div className="max-w-xl mx-auto">
            <h2 className="text-lg font-bold text-white mb-2">
              Chapter {view.chapter.number} — {view.chapter.title}
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              {view.chapter.company} · {view.chapter.devTitle}
            </p>
            <CutscenePanel
              frames={view.chapter.intro}
              onComplete={() => setView({ kind: 'challenges', chapter: view.chapter })}
            />
          </div>
        )}

        {/* ── Challenge List ── */}
        {view.kind === 'challenges' && (
          <div>
            <h2 className="text-lg font-bold text-white mb-1">Challenges</h2>
            <p className="text-sm text-gray-500 mb-6">
              {view.chapter.company} · {view.chapter.devTitle}
            </p>
            {view.chapter.challengeIds.length === 0 ? (
              <p className="text-gray-600 text-sm">No challenges yet for this chapter.</p>
            ) : (
              <div className="flex flex-col gap-3">
                {view.chapter.challengeIds.map((cid, idx) => {
                  const progress = progressMap[cid]
                  const isCompleted = !!progress
                  const isLocked = idx > 0 && !progressMap[view.chapter.challengeIds[idx - 1]]

                  return (
                    <button
                      key={cid}
                      onClick={() => !isLocked && navigate(`/challenge/${cid}`)}
                      disabled={isLocked}
                      className={`w-full text-left border rounded-xl p-4 transition-all ${
                        isLocked
                          ? 'bg-arcade-surface border-arcade-border opacity-40 cursor-not-allowed'
                          : isCompleted
                          ? 'bg-arcade-surface border-arcade-neon/30 hover:border-arcade-neon cursor-pointer'
                          : 'bg-arcade-surface border-arcade-border hover:border-arcade-accent cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className={`font-bold capitalize ${isCompleted ? 'text-white' : 'text-white'}`}>
                            {cid.replace(/-/g, ' ')}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 font-mono">
                            Beginner · Race It
                          </p>
                          {isCompleted && progress.best_time_ms != null && (
                            <p className="text-xs text-gray-600 mt-1 font-mono">
                              Best: {(progress.best_time_ms / 1000).toFixed(1)}s
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                          {isCompleted ? (
                            <>
                              <StarBadge count={progress.stars} />
                              <span className="text-xs px-2 py-0.5 bg-arcade-neon/10 text-arcade-neon rounded font-mono">
                                Replay
                              </span>
                            </>
                          ) : isLocked ? (
                            <Lock size={14} className="text-gray-600" />
                          ) : (
                            <span className="text-xs px-2 py-1 bg-arcade-accent/20 text-violet-300 rounded font-mono">
                              → Start
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  )
}
